// Circular metric gauge — mirrors the Orchestrator's TriggerGauge visual
// language (64px ring, click-to-filter). Lifted as a standalone primitive
// so RFQ Inbox + future surfaces can use it without pulling in the
// TRIGGER_CONFIG registry from the orchestrator package.
import React from 'react';
import { Box, Tooltip, Typography, alpha } from '@mui/material';

interface RfqMetricGaugeProps {
  label: string;
  count: number;
  /** Reference total so the ring fills relative to it (e.g. all RFQs). */
  maxCount: number;
  /** Brand color for the ring + active border. */
  color: string;
  Icon: React.ComponentType<{ sx?: any }>;
  active: boolean;
  onClick?: () => void;
  /** Optional sub-label below the count (e.g. "≥7d", "3 cases"). */
  sublabel?: string;
}

export const RfqMetricGauge: React.FC<RfqMetricGaugeProps> = ({
  label, count, maxCount, color, Icon, active, onClick, sublabel,
}) => {
  const size = 64;
  const stroke = 7;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const fillPct = maxCount > 0 ? Math.min(1, count / maxCount) : 0;
  const offset = circumference * (1 - fillPct);
  const clickable = !!onClick;

  return (
    <Tooltip
      title={count > 0 ? `${label}: ${count}` : `${label}: 0`}
      arrow
      placement="top"
    >
      <Box
        onClick={onClick}
        sx={{
          cursor: clickable ? 'pointer' : 'default',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.625,
          minWidth: 104,
          py: 1.25,
          px: 1.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: active ? alpha(color, 0.25) : 'transparent',
          bgcolor: active ? '#fff' : 'transparent',
          boxShadow: active
            ? `0 1px 2px ${alpha(color, 0.06)}, 0 4px 12px ${alpha(color, 0.10)}`
            : 'none',
          transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
          userSelect: 'none',
          '&:hover': clickable ? {
            bgcolor: active ? '#fff' : alpha(color, 0.04),
            borderColor: active ? alpha(color, 0.35) : alpha(color, 0.18),
            transform: 'translateY(-2px)',
            boxShadow: active
              ? `0 2px 4px ${alpha(color, 0.08)}, 0 6px 16px ${alpha(color, 0.14)}`
              : `0 2px 6px ${alpha(color, 0.08)}`,
          } : undefined,
          '&:active': clickable ? { transform: 'translateY(-1px)' } : undefined,
        }}
      >
        <Box sx={{ position: 'relative', width: size, height: size }}>
          <svg
            width={size}
            height={size}
            style={{ display: 'block', transform: 'rotate(-90deg)' }}
          >
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={alpha(color, 0.12)}
              strokeWidth={stroke}
            />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={color}
              strokeWidth={stroke}
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}
            />
          </svg>
          {/* Center: count, with small icon when count is 0 */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: count >= 1000 ? '0.95rem' : '1.15rem',
                lineHeight: 1,
                color: count > 0 ? color : 'text.disabled',
              }}
            >
              {count}
            </Typography>
            <Icon sx={{ fontSize: 11, color: count > 0 ? color : 'text.disabled', mt: 0.25 }} />
          </Box>
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: active ? color : 'text.secondary',
            textAlign: 'center',
            lineHeight: 1.15,
            fontSize: '0.7rem',
          }}
        >
          {label}
        </Typography>
        {sublabel && (
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.6rem',
              color: 'text.secondary',
              lineHeight: 1,
              mt: -0.25,
            }}
          >
            {sublabel}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default RfqMetricGauge;
